import { Injectable } from '@angular/core';

/**
 * Execution counterpart to DeliverableChangeTrackerService - see that service's doc comment for
 * why dirty state is tracked per-field rather than per-entity. Kept as its own service (not reused
 * for executions) because executions are a separate top-level FormArray with their own id space and
 * their own `_saved` events (executionId, not deliverableId/deliverableType) - see
 * docs/event-tracking-implementation-map.md, "Wave 9 plan: execution & KPI events".
 *
 * Also tracks which executionIds have already had `campaign_creation_execution_added` fired, so the
 * step's silently-created default row (no explicit "Add another" click) can defer firing it until
 * the user makes their first real choice (picking an executionType) without ever double-firing it
 * for rows added via the explicit button, which fire it immediately instead.
 *
 * providedIn: 'root' - must be reset by campaign-form.component.ts alongside
 * DeliverableChangeTrackerService.reset(), for the same reason (no cross-session leakage).
 */
@Injectable({
  providedIn: 'root',
})
export class ExecutionChangeTrackerService {
  // executionId -> set of field names currently net-different from their session-start value.
  private readonly dirtyFieldsByExecution = new Map<string, Set<string>>();
  // executionId -> set of kpi-type sources whose expectedValue is currently net-different from its
  // session-start value - separate from dirtyFieldsByExecution because it feeds a different _saved
  // event (kpi_changed_saved, not execution_changed_saved) with its own baseline diff (see
  // docs/event-tracking-implementation-map.md, "Wave 9 plan").
  private readonly dirtyKpisByExecution = new Map<string, Set<string>>();
  // executionIds that have already had campaign_creation_execution_added fired (explicitly via
  // "Add another", or lazily on first executionType pick for the step's default row).
  private readonly addedExecutionIds = new Set<string>();

  public markFieldChanged(executionId: string | undefined, fieldName: string): void {
    if (!executionId) {
      return;
    }

    const fields = this.dirtyFieldsByExecution.get(executionId) ?? new Set<string>();

    fields.add(fieldName);
    this.dirtyFieldsByExecution.set(executionId, fields);
  }

  public markFieldUnchanged(executionId: string | undefined, fieldName: string): void {
    if (!executionId) {
      return;
    }

    const fields = this.dirtyFieldsByExecution.get(executionId);

    fields?.delete(fieldName);

    if (fields?.size === 0) {
      this.dirtyFieldsByExecution.delete(executionId);
    }
  }

  public isDirty(executionId: string): boolean {
    return !!this.dirtyFieldsByExecution.get(executionId)?.size;
  }

  public markKpiChanged(executionId: string | undefined, kpiSource: string): void {
    if (!executionId) {
      return;
    }

    const kpis = this.dirtyKpisByExecution.get(executionId) ?? new Set<string>();

    kpis.add(kpiSource);
    this.dirtyKpisByExecution.set(executionId, kpis);
  }

  public markKpiUnchanged(executionId: string | undefined, kpiSource: string): void {
    if (!executionId) {
      return;
    }

    const kpis = this.dirtyKpisByExecution.get(executionId);

    kpis?.delete(kpiSource);

    if (kpis?.size === 0) {
      this.dirtyKpisByExecution.delete(executionId);
    }
  }

  public isKpiDirty(executionId: string, kpiSource: string): boolean {
    return !!this.dirtyKpisByExecution.get(executionId)?.has(kpiSource);
  }

  public markExecutionAdded(executionId: string | undefined): void {
    if (!executionId) {
      return;
    }

    this.addedExecutionIds.add(executionId);
  }

  public hasExecutionBeenAdded(executionId: string | undefined): boolean {
    return !!executionId && this.addedExecutionIds.has(executionId);
  }

  public reset(): void {
    this.dirtyFieldsByExecution.clear();
    this.dirtyKpisByExecution.clear();
    this.addedExecutionIds.clear();
  }
}
